import React from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Sparkles, Leaf, Instagram, Facebook, Twitter, Music2, MessageCircle } from "lucide-react";
import { useSettings, telHref, waHref } from "@/context/SettingsContext";

const GLASS_IMG = "/brand/girl-crop.jpg";

const features = [
  { icon: ShieldCheck, title: "Reverse Osmosis + UV", text: "Multi-stage purification removes impurities for a pure, safe sip every time." },
  { icon: Sparkles, title: "Crisp, Clean Taste", text: "Mineral-balanced water that tastes as refreshing as it looks." },
  { icon: Leaf, title: "Eco-Friendly Swaps", text: "Reusable carboys and bottle exchange cut single-use plastic waste." },
];

export const AboutSection = () => (
  <section id="about" className="py-24 bg-slate-50">
    <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
      <motion.div
        initial={{ opacity: 0, x: -30 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="relative rounded-3xl overflow-hidden aspect-[4/3] shadow-2xl"
      >
        <img src={GLASS_IMG} alt="Customer holding a Cool Waves Water bottle" className="w-full h-full object-cover" data-testid="about-image" />
        <div className="absolute inset-0 bg-gradient-to-tr from-sky-950/40 to-transparent" />
      </motion.div>

      <div>
        <span className="text-xs font-semibold uppercase tracking-[0.25em] text-sky-600">Our Purity Promise</span>
        <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 mt-3">
          Water you can trust, <span className="cw-text-gradient">taste you'll love.</span>
        </h2>
        <p className="mt-4 text-slate-600 leading-relaxed">
          Cool Waves Water is carefully purified for a crisp, clean taste in every size — serving Jamaican homes and
          businesses with dependable hydration, from everyday bottles to 5 gallon supply.
        </p>
        <div className="mt-8 space-y-5">
          {features.map((f, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.1 }}
              className="flex gap-4"
            >
              <div className="w-11 h-11 shrink-0 rounded-2xl cw-ocean-gradient flex items-center justify-center">
                <f.icon className="w-5 h-5 text-sky-200" />
              </div>
              <div>
                <h3 className="font-display font-bold text-slate-900">{f.title}</h3>
                <p className="text-slate-600 text-sm mt-0.5">{f.text}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const SOCIAL_ICONS = { instagram: Instagram, facebook: Facebook, twitter: Twitter, tiktok: Music2 };

export const Footer = () => {
  const { company } = useSettings();
  const c = company || {};
  const socials = Object.entries(c.socials || {}).filter(([, url]) => url);
  return (
  <footer className="cw-ocean-gradient text-sky-100/80 pt-16 pb-8" data-testid="site-footer">
    <div className="max-w-7xl mx-auto px-5 lg:px-8 grid md:grid-cols-3 gap-10">
      <div>
        <div className="font-display text-2xl font-extrabold text-white">Cool Waves Water</div>
        <p className="mt-3 text-sm max-w-xs leading-relaxed">
          Carefully purified for a crisp, clean taste in every size. Proudly serving Kingston, St. Andrew & beyond.
        </p>
        {socials.length > 0 && (
          <div className="flex gap-2 mt-4" data-testid="footer-socials">
            {socials.map(([k, url]) => {
              const Icon = SOCIAL_ICONS[k] || Sparkles;
              return (
                <a key={k} href={url} target="_blank" rel="noreferrer" data-testid={`footer-social-${k}`} className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors">
                  <Icon className="w-4 h-4" />
                </a>
              );
            })}
          </div>
        )}
      </div>
      <div>
        <h4 className="font-display font-bold text-white mb-3">Contact</h4>
        <ul className="space-y-2 text-sm" data-testid="footer-contact">
          {c.phone && <li><a href={telHref(c.phone)} className="hover:text-white">Phone: {c.phone}</a></li>}
          {c.whatsapp && <li><a href={waHref(c.whatsapp)} target="_blank" rel="noreferrer" className="hover:text-white inline-flex items-center gap-1"><MessageCircle className="w-3.5 h-3.5" /> WhatsApp: {c.whatsapp}</a></li>}
          {c.email && <li><a href={`mailto:${c.email}`} className="hover:text-white">Email: {c.email}</a></li>}
          {c.address && <li>{c.address}</li>}
          
        </ul>
      </div>
      <div>
        <h4 className="font-display font-bold text-white mb-3">Delivery Hours</h4>
        <ul className="space-y-2 text-sm" data-testid="footer-hours">
          {(c.hours || []).map((h, i) => <li key={i}>{h.day}: {h.time}</li>)}
        </ul>
      </div>
    </div>
    <div className="max-w-7xl mx-auto px-5 lg:px-8 mt-12 pt-6 border-t border-sky-300/15 text-xs flex flex-col sm:flex-row justify-between gap-2">
      <span>© {new Date().getFullYear()} Cool Waves Water Ltd. All rights reserved.</span>
      <span>{c.tagline || "Pure Hydration. Island Fresh."}</span>
      <span><a href="/admin" className="inline-flex items-center gap-1 hover:text-white"><ShieldCheck className="w-3.5 h-3.5" /> Admin Portal</a></span>
    </div>
  </footer>
  );
};
